import React from 'react';
import { Button, Modal } from '@economic/gaudi';
import { Row, Column } from '../../../../shared/components/Grid/Grid';
import internalApiClient from '../../../../shared/util/api/internalApiClient';
import { Wizard } from './Wizard/Wizard';
import CoverImageDanish from './images/userpanel_cover_da.png';
import CoverImageEnglish from './images/userpanel_cover_en.png';
import { t } from '../../../../shared/components/l10n/l10n';
import { UserPanelRegistrationInformation } from './types';
import ConfirmationDialog from '../../../../shared/components/modals/ConfirmationDialog';
import { participationMethods, languages, hasEnLangCode, InterestAreas } from './util';
import { InterestAreasModel } from './InterestAreasModel';
import { useCurrentLocale } from '@economic/react-app';

interface Props {
    data: UserPanelRegistrationInformation;
    refresh: () => void;
}

export const Home = ({ data, refresh }: Props) => {
    const locale = useCurrentLocale();
    const image = hasEnLangCode(locale.languageCode) ? CoverImageEnglish : CoverImageDanish;

    const handleDeregister = async () => {
        try {
            await internalApiClient.delete(`/user-panel`);
            refresh();
        } catch {
            // skip
        }
    };

    const EditButton = ({ step }: { step: number }) => {
        return (
            <Button
                appearance="discrete"
                className="float-right text-sm"
                modal={modalProps => (
                    <Modal {...modalProps} style={{ width: '700px' }}>
                        {({ hide }) => <Wizard data={data} onCancel={hide} step={step} refresh={refresh} />}
                    </Modal>
                )}>
                {t`USER_PANEL_EDIT_BUTTON`}
            </Button>
        );
    };

    const EditButtonAreasOfInterest = () => {
        return (
            <Button
                appearance="discrete"
                className="float-right text-sm"
                modal={modalProps => (
                    <Modal {...modalProps} size="xs">
                        <InterestAreasModel data={data} refresh={refresh} />
                    </Modal>
                )}>
                {t`USER_PANEL_EDIT_BUTTON`}
            </Button>
        );
    };

    return (
        <div className="view__user_panel__registered">
            <Row>
                <Column>
                    <div className="view__user_panel__container_left">
                        <div>
                            <div className="view__user_panel__info_card__border">
                                <div className="view__user_panel__content view__user_panel__image">
                                    <img src={image} />
                                </div>

                                <div className="view__user_panel__content">
                                    <p>{t`USER_PANEL_HERO_INFORMATION`}</p>
                                    <br />
                                </div>
                            </div>

                            <div className="view__user_panel__info_card__border view__user_panel__survey">
                                <div className="view__user_panel__content view__user_panel__survey">
                                    <div className="mb-6">{t`USER_PANEL_SURVEY_INFORMATION`}</div>
                                    <div>
                                        <Button
                                            appearance="primary"
                                            href="https://docs.google.com/forms/d/e/1FAIpQLScgHTrRcMI1xBdSOnkcRsU1hzHaFnDalp3Zqe0l3MwfrxNy7A/viewform"
                                            target="_blank">
                                            {t`USER_PANEL_SURVEY_BUTTON`}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Column>
                <Column>
                    <div className="view__user_panel__container_right">
                        <div className="view__user_panel__edit">
                            <div>
                                <h3>
                                    {t`USER_PANEL_INFORMATION`}
                                    <EditButton step={1} />
                                </h3>

                                <p>{data.name}</p>
                                <p>{data.email}</p>
                                <p>{data.phone}</p>
                                <p>{data.city}</p>
                                <p>{data.jobRole}</p>

                                <h3>
                                    {t`USER_PANEL_INFORMATION_LANGUAGES`}
                                    <EditButton step={2} />
                                </h3>

                                {Object.keys(data.languages)
                                    .filter(language => !!data.languages[language])
                                    .map(language => (
                                        <p key={language}>{t(languages[language])}</p>
                                    ))}

                                <h3>
                                    {t`USER_PANEL_INFORMATION_PARTICIPATION`}
                                    <EditButton step={3} />
                                </h3>

                                {Object.keys(data.participation)
                                    .filter(key => !!data.participation[key])
                                    .map(key => (
                                        <p key={key}>{t(participationMethods[key])}</p>
                                    ))}

                                <h3>
                                    {t`USER_PANEL_AREASOFINTEREST`}
                                    <EditButtonAreasOfInterest />
                                </h3>

                                {data.interestAreas &&
                                    Object.keys(data.interestAreas)
                                        .filter(key => !!data.interestAreas[key])
                                        .map(key => <p key={key}>{t(InterestAreas[key])}</p>)}
                            </div>
                            <div>
                                <Button
                                    appearance="discrete"
                                    className="float-right mb-16"
                                    modal={modalProps => (
                                        <Modal {...modalProps} style={{ width: '500px' }}>
                                            {({ hide }) => (
                                                <ConfirmationDialog
                                                    label="Deactivate"
                                                    message={t`USER_PANEL_DEACTIVATE_ARE_YOU_SURE`}
                                                    onCancel={hide}
                                                    onConfirm={handleDeregister}
                                                />
                                            )}
                                        </Modal>
                                    )}>
                                    {t`USER_PANEL_DEACTIVATE_BUTTON`}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Column>
            </Row>
        </div>
    );
};
